import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Row, Col, Card, CardBody, Button, ButtonGroup } from "shards-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { startOfDay, endOfDay, subDays } from "date-fns";

/**
 * Enhanced Date Range Picker with improved usability
 * - Clear visual indicators for date range
 * - Mobile-friendly design
 * - Accessible controls
 * - Clear labels and instructions
 */
const EnhancedDateRangePicker = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  startLabel = "From",
  endLabel = "To",
  maxDate,
  minDate,
  className,
  showClearButtons = true,
  disabled = false,
  showStartCalendarOnly = false,
  showEndCalendarOnly = false
}) => {
  const [localStartDate, setLocalStartDate] = useState(startDate);
  const [localEndDate, setLocalEndDate] = useState(endDate);
  const [activeSelector, setActiveSelector] = useState(showStartCalendarOnly || showEndCalendarOnly ? 'both' : null);

  // Update local state when props change
  useEffect(() => {
    setLocalStartDate(startDate);
  }, [startDate]);

  useEffect(() => {
    setLocalEndDate(endDate);
  }, [endDate]);

  // Close the datepicker on outside clicks
  useEffect(() => {
    const handleClickOutside = (e) => {
      const isDatePickerClick = e.target.closest('.enhanced-date-range, .react-datepicker');
      if (!isDatePickerClick && activeSelector) {
        setActiveSelector(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeSelector]);

  // Compact display of the selected date range
  const CompactDisplay = () => {
    const formatDate = (date) => {
      if (!date) return '';
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const startDateText = formatDate(isValidDate(localStartDate) ? localStartDate : null);
    const endDateText = formatDate(isValidDate(localEndDate) ? localEndDate : null);
    const hasDateRange = startDateText || endDateText;
    
    // Minimal display when no dates are selected
    if (!hasDateRange) {
      return (
        <div 
          className="enhanced-date-range__compact-minimal"
          onClick={() => setActiveSelector('start')}
          aria-label="Select date range"
          role="button"
          tabIndex={0}
        >
          <div className="enhanced-date-range__compact-icon">📅</div>
          <div className="enhanced-date-range__compact-placeholder">
            Select Date Range
          </div>
        </div>
      );
    }
    
    // Rich display when dates are selected
    return (
      <div 
        className="enhanced-date-range__compact has-dates"
        onClick={() => setActiveSelector('start')}
        aria-label="Select date range"
        role="button"
        tabIndex={0}
      >
        <div className="enhanced-date-range__compact-icon">📅</div>
        <div className="enhanced-date-range__compact-text">
          {startDateText} - {endDateText || 'Present'}
        </div>
        
        {showClearButtons && !disabled && (
          <button
            type="button"
            className="enhanced-date-range__compact-clear"
            onClick={(e) => {
              e.stopPropagation();
              handleStartDateChange(null);
              handleEndDateChange(null);
            }}
            aria-label="Clear date range"
          >
            ×
          </button>
        )}
      </div>
    );
  };

  // Custom input components with clear buttons
  const StartDateInput = React.forwardRef(({ value, onClick }, ref) => (
    <div className="enhanced-date-range__input-container">
      <label id="start-date-label" className="enhanced-date-range__label">
        {startLabel}
      </label>
      <div className="enhanced-date-range__input-wrapper">
        <input
          ref={ref}
          className="enhanced-date-range__input form-control"
          value={value}
          onClick={onClick}
          readOnly
          aria-labelledby="start-date-label"
          disabled={disabled}
        />
        {showClearButtons && value && !disabled && (
          <button
            type="button"
            className="enhanced-date-range__clear-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleStartDateChange(null);
            }}
            aria-label="Clear start date"
          >
            ×
          </button>
        )}
      </div>
    </div>
  ));

  const EndDateInput = React.forwardRef(({ value, onClick }, ref) => (
    <div className="enhanced-date-range__input-container">
      <label id="end-date-label" className="enhanced-date-range__label">
        {endLabel}
      </label>
      <div className="enhanced-date-range__input-wrapper">
        <input
          ref={ref}
          className="enhanced-date-range__input form-control"
          value={value}
          onClick={onClick}
          readOnly
          aria-labelledby="end-date-label"
          disabled={disabled}
        />
        {showClearButtons && value && !disabled && (
          <button
            type="button"
            className="enhanced-date-range__clear-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleEndDateChange(null);
            }}
            aria-label="Clear end date"
          >
            ×
          </button>
        )}
      </div>
    </div>
  ));

  StartDateInput.displayName = 'StartDateInput';
  EndDateInput.displayName = 'EndDateInput';

  // Helper function to validate dates
  const isValidDate = (date) => date instanceof Date && !isNaN(date);
  
  // Ensure we have valid dates for our local state and props
  const validStartDate = isValidDate(startDate) ? startDate : null;
  const validEndDate = isValidDate(endDate) ? endDate : null;
  const validMinDate = isValidDate(minDate) ? minDate : null;
  const validMaxDate = isValidDate(maxDate) ? maxDate : null;

  // Change handlers with validation
  const handleStartDateChange = (date) => {
    if (date === null || isValidDate(date)) {
      setLocalStartDate(date);
      onStartDateChange(date);
    } else {
      console.warn('Invalid start date prevented:', date);
    }
  };

  const handleEndDateChange = (date) => {
    if (date === null || isValidDate(date)) {
      setLocalEndDate(date);
      onEndDateChange(date);
    } else {
      console.warn('Invalid end date prevented:', date);
    }
  };

  // When start date is selected, automatically show end date picker
  const handleDateSelect = (date, pickerType) => {
    if (pickerType === 'start') {
      handleStartDateChange(date);
      if (date) {
        setActiveSelector('end');
      }
    } else {
      handleEndDateChange(date);
      setActiveSelector(null); // Close after end date is selected
    }
  };

  // Preset quick selections for date ranges
  const applyPreset = (preset) => {
    const today = new Date();
    let start, end;
    
    switch(preset) {
      case 'today':
        start = startOfDay(today);
        end = endOfDay(today);
        break;
      case 'yesterday':
        start = startOfDay(subDays(today, 1));
        end = endOfDay(subDays(today, 1));
        break;
      case 'last7':
        start = startOfDay(subDays(today, 6));
        end = endOfDay(today);
        break;
      case 'last30':
        start = startOfDay(subDays(today, 29));
        end = endOfDay(today);
        break;
      default:
        return;
    }
    
    handleStartDateChange(start);
    handleEndDateChange(end);
    setActiveSelector(null); // Close the popover after selection
  };

  return (
    <div className={`enhanced-date-range ${className || ""}`}>
      <style>
        {`
          .enhanced-date-range {
            position: relative;
            margin-bottom: 1rem;
          }
          .enhanced-date-range__input-container {
            flex: 1;
            min-width: 140px;
            max-width: 200px;
          }
          .enhanced-date-range__label {
            display: block;
            margin-bottom: 0.25rem;
            font-weight: 500;
            color: #3d5170;
            font-size: 0.9rem;
          }
          .enhanced-date-range__input-wrapper {
            position: relative;
            display: flex;
            align-items: center;
            height: 38px; /* Fixed height for consistency */
          }
          .enhanced-date-range__input {
            flex: 1;
            border-color: #e1e5eb;
            background-color: #fff;
            padding-right: 30px;
            height: 100%;
            font-size: 0.9rem;
          }
          .enhanced-date-range__input:focus {
            border-color: #007bff;
            box-shadow: 0 0.313rem 0.719rem rgba(0, 123, 255, 0.1), 0 0.156rem 0.125rem rgba(0, 0, 0, 0.06);
          }
          .enhanced-date-range__clear-btn {
            position: absolute;
            right: 10px;
            background: transparent;
            border: none;
            font-size: 1.5rem;
            line-height: 1;
            padding: 0;
            cursor: pointer;
            color: #818ea3;
            display: flex;
            align-items: center;
            justify-content: center;
            height: 100%;
          }
          .enhanced-date-range__clear-btn:hover {
            color: #3d5170;
          }
          
          /* Compact display styling */
          .enhanced-date-range__compact,
          .enhanced-date-range__compact-minimal {
            display: flex;
            align-items: center;
            height: 38px;
            border: 1px solid #e1e5eb;
            border-radius: 0.25rem;
            padding: 0 12px;
            background-color: #fff;
            cursor: pointer;
            transition: all 0.2s;
            max-width: 320px;
          }
          
          /* Minimal styling (when no dates selected) */
          .enhanced-date-range__compact-minimal {
            width: auto;
            display: inline-flex;
            min-width: 120px;
          }
          .enhanced-date-range__compact-minimal .enhanced-date-range__compact-placeholder {
            flex: 0;
            display: none; /* Hide text by default */
          }
          .enhanced-date-range__compact-minimal:hover .enhanced-date-range__compact-placeholder {
            display: block; /* Show text on hover */
            margin-left: 8px;
            animation: fadeIn 0.2s;
          }
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }

          /* Hover states */
          .enhanced-date-range__compact:hover,
          .enhanced-date-range__compact-minimal:hover {
            border-color: #007bff;
            box-shadow: 0 0.313rem 0.719rem rgba(0, 123, 255, 0.1), 0 0.156rem 0.125rem rgba(0, 0, 0, 0.06);
          }
          
          /* Styling when dates are selected */
          .enhanced-date-range__compact.has-dates {
            border-color: #21AA73;
            background-color: rgba(33, 170, 115, 0.05);
          }
          
          /* Common elements */
          .enhanced-date-range__compact-icon {
            margin-right: 10px;
            font-size: 1rem;
          }
          .enhanced-date-range__compact-text {
            flex: 1;
            font-size: 0.9rem;
            color: #495057;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .enhanced-date-range__compact-placeholder {
            flex: 1;
            font-size: 0.9rem;
            color: #818ea3;
          }
          .enhanced-date-range__compact-clear {
            background: transparent;
            border: none;
            font-size: 1.5rem;
            line-height: 1;
            padding: 0;
            margin-left: 8px;
            cursor: pointer;
            color: #818ea3;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .enhanced-date-range__compact-clear:hover {
            color: #3d5170;
          }
          
          /* Popover positioning */
          .enhanced-date-range__popover {
            position: absolute;
            top: 45px;
            left: 0;
            z-index: 1000;
            background: white;
            border-radius: 0.375rem;
            box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
            border: 1px solid #e1e5eb;
            padding: 15px;
            display: flex;
            flex-direction: column;
          }
          
          /* Preset buttons styling */
          .enhanced-date-range__presets {
            margin-top: 15px;
            padding-top: 12px;
            border-top: 1px solid #e1e5eb;
          }
          .enhanced-date-range__presets-title {
            font-size: 0.8rem;
            color: #818ea3;
            margin-bottom: 8px;
          }
          .enhanced-date-range__presets-buttons {
            display: flex;
            flex-wrap: wrap;
            gap: 4px;
          }
          .enhanced-date-range__preset-btn {
            font-size: 11px !important;
            padding: 4px 8px !important;
            color: #21AA73 !important;
            border-color: rgba(33, 170, 115, 0.3) !important;
          }
          
          /* Pickers container */
          .enhanced-date-range__pickers {
            display: flex;
            gap: 15px;
          }
          
          /* Improve the datepicker popup */
          .react-datepicker {
            font-family: inherit;
            border-radius: 0.375rem;
            border: 1px solid #e1e5eb;
            box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
          }
          .react-datepicker__header {
            background-color: #f5f6f8;
            border-bottom: 1px solid #e1e5eb;
          }
          .react-datepicker__navigation {
            top: 12px;
          }
          .react-datepicker__day--selected,
          .react-datepicker__day--keyboard-selected,
          .react-datepicker__day--in-range,
          .react-datepicker__day--in-selecting-range {
            background-color: #007bff;
            border-radius: 0;
            color: white;
          }
          .react-datepicker__day--range-start,
          .react-datepicker__day--range-end,
          .react-datepicker__day--selecting-range-start,
          .react-datepicker__day--selecting-range-end {
            background-color: #0056b3;
            border-radius: 50%;
          }
          .react-datepicker__day:hover {
            background-color: #e6f2ff;
            border-radius: 0.375rem;
          }
          
          /* Mobile improvements */
          @media (max-width: 576px) {
            .enhanced-date-range__popover {
              width: calc(100vw - 30px);
              left: 50%;
              transform: translateX(-50%);
            }
            .enhanced-date-range__pickers {
              flex-direction: column;
            }
          }
          
          /* Side by side calendar styles */
          .enhanced-date-range__side-by-side {
            display: flex;
            gap: 20px;
          }
          
          .enhanced-date-range__calendar-container {
            flex: 1;
          }
          
          .enhanced-date-range__calendar-label {
            font-size: 13px;
            color: #818ea3;
            margin-bottom: 10px;
            display: block;
          }
          
          /* Adjust datepicker styles for side by side */
          .react-datepicker-wrapper {
            width: 100%;
          }
          
          .react-datepicker__month-container {
            float: none;
          }
          
          /* Mobile improvements */
          @media (max-width: 576px) {
            .enhanced-date-range__side-by-side {
              flex-direction: column;
            }
          }
        `}
      </style>

      {/* Show either the compact display or the calendars based on props */}
      {(!showStartCalendarOnly && !showEndCalendarOnly) ? (
        <>
          {/* Compact display that shows when no selector is active */}
          {!activeSelector && <CompactDisplay />}

          {/* Popup with date selectors */}
          {activeSelector && (
            <div className="enhanced-date-range__popover">
              {/* Date Pickers */}
              <div className="enhanced-date-range__pickers">
                <DatePicker
                  selected={isValidDate(localStartDate) ? localStartDate : null}
                  onChange={(date) => handleDateSelect(date, 'start')}
                  selectsStart
                  startDate={localStartDate}
                  endDate={localEndDate}
                  maxDate={localEndDate || validMaxDate}
                  minDate={validMinDate}
                  customInput={<StartDateInput />}
                  disabled={disabled}
                  inline
                />
                <DatePicker
                  selected={isValidDate(localEndDate) ? localEndDate : null}
                  onChange={(date) => handleDateSelect(date, 'end')}
                  selectsEnd
                  startDate={localStartDate}
                  endDate={localEndDate}
                  minDate={localStartDate}
                  maxDate={validMaxDate}
                  customInput={<EndDateInput />}
                  disabled={disabled}
                  inline
                />
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="enhanced-date-range__calendar-container">
          {showStartCalendarOnly && (
            <>
              <span className="enhanced-date-range__calendar-label">{startLabel}</span>
              <DatePicker
                selected={isValidDate(localStartDate) ? localStartDate : null}
                onChange={(date) => handleStartDateChange(date)}
                selectsStart
                startDate={localStartDate}
                endDate={localEndDate}
                maxDate={localEndDate || validMaxDate}
                minDate={validMinDate}
                disabled={disabled}
                inline
              />
            </>
          )}
          {showEndCalendarOnly && (
            <>
              <span className="enhanced-date-range__calendar-label">{endLabel}</span>
              <DatePicker
                selected={isValidDate(localEndDate) ? localEndDate : null}
                onChange={(date) => handleEndDateChange(date)}
                selectsEnd
                startDate={localStartDate}
                endDate={localEndDate}
                minDate={localStartDate}
                maxDate={validMaxDate}
                disabled={disabled}
                inline
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

EnhancedDateRangePicker.propTypes = {
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  onStartDateChange: PropTypes.func.isRequired,
  onEndDateChange: PropTypes.func.isRequired,
  startLabel: PropTypes.string,
  endLabel: PropTypes.string,
  maxDate: PropTypes.instanceOf(Date),
  minDate: PropTypes.instanceOf(Date),
  className: PropTypes.string,
  showClearButtons: PropTypes.bool,
  disabled: PropTypes.bool,
  showStartCalendarOnly: PropTypes.bool,
  showEndCalendarOnly: PropTypes.bool
};

export default EnhancedDateRangePicker;
